import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} DialogRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [scrollArea]
 *
 * @extends {Component<DialogRefs>}
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {number | null} */
  #scrollHintRafId = null;

  connectedCallback() {
    super.connectedCallback();

    const scrollArea = this.refs.scrollArea;
    if (scrollArea instanceof HTMLElement) {
      scrollArea.addEventListener('scroll', this.#handleScrollAreaScroll);
      this.#updateScrollHint();
    }

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const scrollArea = this.refs.scrollArea;
    if (scrollArea instanceof HTMLElement) {
      scrollArea.removeEventListener('scroll', this.#handleScrollAreaScroll);
    }

    if (this.#scrollHintRafId !== null) {
      cancelAnimationFrame(this.#scrollHintRafId);
      this.#scrollHintRafId = null;
    }

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;
    if (!minWidth && !maxWidth) return;

    const width = window.innerWidth;
    if ((minWidth && width < minWidth) || (maxWidth && width > maxWidth)) {
      this.closeDialog();
    }
  }, 50);

  #handleScrollAreaScroll = () => {
    if (this.#scrollHintRafId !== null) return;

    this.#scrollHintRafId = requestAnimationFrame(() => {
      this.#scrollHintRafId = null;
      this.#updateScrollHint();
    });
  };

  #updateScrollHint() {
    const scrollArea = this.refs.scrollArea;
    if (!(scrollArea instanceof HTMLElement)) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollArea;
    const scrollRatio = scrollTop / (scrollHeight - clientHeight);

    scrollArea.style.maskImage = Number.isNaN(scrollRatio)
      ? ''
      : `linear-gradient(
          to bottom,
          transparent ${scrollRatio > 0 ? 1 : 0}%,
          black ${scrollRatio < 0.1 ? scrollRatio * 100 : 10}%,
          black ${scrollRatio > 0.9 ? scrollRatio * 100 : 90}%,
          transparent 100%
        )`;
  }

  /**
   * @param {Object} [options]
   * @param {boolean} [options.immediate] - Skip animation and requestAnimationFrame (used during drawer swap)
   */
  showDialog({ immediate = false } = {}) {
    const { dialog } = this.refs;
    if (dialog.open) return;

    const open = () => {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      dialog.setAttribute('scroll-lock', '');

      dialog.showModal();

      this.#updateScrollHint();

      // Safari/iOS incorrectly matches :focus-visible on programmatic focus.
      // Suppress outline until the user presses a key.
      dialog.dataset.suppressFocus = '';
      dialog.addEventListener('keydown', () => {
        delete dialog.dataset.suppressFocus;
      }, { once: true });

      if (!immediate) {
        dialog.classList.add('dialog-opening');
        onAnimationEnd(dialog, undefined, { subtree: false }).then(() => {
          dialog.classList.remove('dialog-opening');
        });
      }

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    };

    if (immediate) {
      open();
    } else {
      requestAnimationFrame(open);
    }
  }

  /**
   * @param {Object} [options]
   * @param {boolean} [options.immediate] - Skip animation and scroll restore (used during drawer swap)
   */
  closeDialog = async ({ immediate = false } = {}) => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    if (!immediate) {
      dialog.classList.add('dialog-closing');
      await onAnimationEnd(dialog, undefined, { subtree: false });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    if (!immediate) {
      dialog.removeAttribute('scroll-lock');
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) {
      this.closeDialog();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}
